'use client';

import { useState, useEffect } from 'react';
import axios from 'axios';
import SearchableUserSelect from './searchableUserSelect';
const AssetsReport = () => {
  const [usersSummary, setUsersSummary] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userReport, setUserReport] = useState(null);
  const [userMenuStructure, setUserMenuStructure] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterLanguage, setFilterLanguage] = useState('all');
  const [showMenuStructure, setShowMenuStructure] = useState(false);
  const [sortBy, setSortBy] = useState('totalAssets');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState(null);
  const [fileTypeFilter, setFileTypeFilter] = useState('all'); // 'all', 'images', 'videos'

  // Fetch users summary on component mount
  useEffect(() => {
    fetchUsersSummary();
  }, []);

  const fetchUsersSummary = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/users-assets-summary');
      const data = await response.json();
      setUsersSummary(data);
    } catch (error) {
      console.error('Error fetching users summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserReport = async userId => {
    try {
      setLoading(true);
      const response = await fetch(`/api/assets-report/${userId}`);
      const data = await response.json();
      setUserReport(data);
    } catch (error) {
      console.error('Error fetching user report:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserMenuStructure = async userId => {
    try {
      const response = await fetch(`/api/user-menu-structure/${userId}`);
      const data = await response.json();
      setUserMenuStructure(data);
    } catch (error) {
      console.error('Error fetching user menu structure:', error);
    }
  };

  const handleUserSelect = userId => {
    setSelectedUserId(userId);
    setCurrentPage(1); // Reset pagination when selecting new user
    setFilterLanguage('all'); // Reset language filter
    setFileTypeFilter('all'); // Reset file type filter
    fetchUserReport(userId);
    fetchUserMenuStructure(userId);
  };

  // Helper function to determine file type
  const getFileType = (asset) => {
    const fileName = asset.fileName?.toLowerCase() || '';
    const fileUrl = asset.fileUrl?.toLowerCase() || '';
    const field = asset.field?.toLowerCase() || '';
    
    // Check by file extension
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.ico'];
    const videoExtensions = ['.mp4', '.mov', '.avi', '.wmv', '.flv', '.webm', '.mkv', '.m4v'];
    
    const hasImageExt = imageExtensions.some(ext => fileName.endsWith(ext) || fileUrl.includes(ext));
    const hasVideoExt = videoExtensions.some(ext => fileName.endsWith(ext) || fileUrl.includes(ext));
    
    if (hasImageExt) return 'image';
    if (hasVideoExt) return 'video';
    
    // Check by field name
    if (field.includes('video') || field.includes('avatar')) return 'video';
    if (field.includes('image') || field.includes('logo') || field.includes('photo') || 
        field.includes('icon') || field.includes('qr') || field.includes('png') || 
        field.includes('button')) return 'image';
    
    // Check by content type
    if (asset.contentType === 'video') return 'video';
    if (asset.contentType === 'image') return 'image';
    
    return 'unknown';
  };

  const getFilteredAssets = () => {
    if (!userReport) return [];

    let filtered = [];
    if (filterCategory === 'all') {
      filtered = userReport.assetDetails;
    } else {
      filtered = userReport.assetDetails.filter(asset =>
        asset.category.toLowerCase().includes(filterCategory.toLowerCase())
      );
    }

    // Apply language filter
    if (filterLanguage !== 'all') {
      filtered = filtered.filter(asset => asset.language === filterLanguage);
    }

    // Apply file type filter
    if (fileTypeFilter !== 'all') {
      filtered = filtered.filter(asset => {
        const fileType = getFileType(asset);
        if (fileTypeFilter === 'images') {
          return fileType === 'image';
        } else if (fileTypeFilter === 'videos') {
          return fileType === 'video';
        }
        return true;
      });
    }

    return filtered;
  };

  const getAvailableLanguages = () => {
    if (!userReport) return [];
    
    const languages = new Set();
    userReport.assetDetails.forEach(asset => {
      if (asset.language) {
        languages.add(asset.language);
      }
    });
    
    return Array.from(languages).sort();
  };

  const getFileTypeCounts = () => {
    if (!userReport) return { images: 0, videos: 0, total: 0 };
    
    let images = 0;
    let videos = 0;
    
    // Apply current category and language filters
    let filtered = userReport.assetDetails;
    if (filterCategory !== 'all') {
      filtered = filtered.filter(asset =>
        asset.category.toLowerCase().includes(filterCategory.toLowerCase())
      );
    }
    if (filterLanguage !== 'all') {
      filtered = filtered.filter(asset => asset.language === filterLanguage);
    }
    
    // Count only assets with files
    filtered.filter(a => a.hasFile).forEach(asset => {
      const fileType = getFileType(asset);
      if (fileType === 'image') images++;
      else if (fileType === 'video') videos++;
    });
    
    return { images, videos, total: images + videos };
  };

  const getPaginatedAssets = () => {
    const filtered = getFilteredAssets();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const filtered = getFilteredAssets();
    return Math.ceil(filtered.length / itemsPerPage);
  };

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = newItemsPerPage => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const getSortedUsers = () => {
    return [...usersSummary].sort((a, b) => {
      switch (sortBy) {
        case 'totalAssets':
          return b.totalAssets - a.totalAssets;
        case 'enabledAssets':
          return b.enabledAssets - a.enabledAssets;
        case 'userName':
          return a.userName.localeCompare(b.userName);
        default:
          return b.totalAssets - a.totalAssets;
      }
    });
  };

  const copyToClipboard = text => {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Copied to clipboard:', text);
    });
  };

  const confirmDelete = async (rowid, filename, key, category, language) => {
    if (window.confirm('Are you sure you want to delete this asset?')) {
      setIsDeleting(true);

      try {
        const response = await axios.post('/api/deleteassets', {
          rowid,
          filename,
          key,
          category,
          language,
        });

        await fetchUserReport(selectedUserId);

        setIsDeleting(false);
      } catch (error) {
        console.error('Error submitting form:', error);
        setIsDeleting(false);
      }
    }
  };

  // Bulk delete functions
  const handleSelectAsset = (asset) => {
    setSelectedAssets(prev => {
      const isSelected = prev.some(a => a.rowid === asset.rowid && a.field === asset.field && a.language === asset.language);
      if (isSelected) {
        return prev.filter(a => !(a.rowid === asset.rowid && a.field === asset.field && a.language === asset.language));
      } else {
        return [...prev, asset];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedAssets([]);
    } else {
      const assetsWithFiles = getFilteredAssets().filter(a => a.hasFile);
      setSelectedAssets(assetsWithFiles);
    }
    setSelectAll(!selectAll);
  };

  const handleBulkDelete = async () => {
    if (selectedAssets.length === 0) {
      alert('Please select assets to delete');
      return;
    }

    // Count file types in selection
    let imageCount = 0;
    let videoCount = 0;
    selectedAssets.forEach(asset => {
      const fileType = getFileType(asset);
      if (fileType === 'image') imageCount++;
      else if (fileType === 'video') videoCount++;
    });

    const fileTypeBreakdown = [];
    if (imageCount > 0) fileTypeBreakdown.push(`${imageCount} image(s)`);
    if (videoCount > 0) fileTypeBreakdown.push(`${videoCount} video(s)`);
    const fileTypeText = fileTypeBreakdown.join(' and ') || `${selectedAssets.length} file(s)`;

    const confirmMessage = `Are you sure you want to delete ${selectedAssets.length} asset(s)?\n\nBreakdown: ${fileTypeText}\n\nThis will:\n- Delete files from AWS S3\n- Remove asset references from database\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsDeleting(true);
    setBulkDeleteProgress({ current: 0, total: selectedAssets.length, status: 'processing' });

    try {
      const response = await axios.post('/api/bulk-delete-assets', {
        assets: selectedAssets
      });

      setBulkDeleteProgress({
        current: response.data.total,
        total: response.data.total,
        status: 'completed',
        successful: response.data.successful,
        failed: response.data.failed,
        details: response.data.details
      });

      // Refresh the report
      await fetchUserReport(selectedUserId);

      // Clear selections
      setSelectedAssets([]);
      setSelectAll(false);

      // Show results
      alert(`Bulk delete completed!\n\nSuccessful: ${response.data.successful}\nFailed: ${response.data.failed}\n\nCheck console for details.`);
      console.log('Bulk delete results:', response.data);

    } catch (error) {
      console.error('Error in bulk delete:', error);
      alert('Error deleting assets. Check console for details.');
      setBulkDeleteProgress({ status: 'error', error: error.message });
    } finally {
      setIsDeleting(false);
      setTimeout(() => setBulkDeleteProgress(null), 5000);
    }
  };

  const isAssetSelected = (asset) => {
    return selectedAssets.some(a => a.rowid === asset.rowid && a.field === asset.field && a.language === asset.language);
  };

  const handleDeleteClient = async () => {
    if (!selectedUserId) {
      alert('Please select a user first');
      return;
    }

    const user = usersSummary.find(u => u.userId === selectedUserId);
    const userName = user?.userName || 'Unknown User';
    const userEmail = user?.userEmail || '';

    const confirmMessage = `⚠️ DANGER: DELETE CLIENT ⚠️\n\nYou are about to permanently delete:\n\nClient: ${userName}\nEmail: ${userEmail}\nUser ID: ${selectedUserId}\n\nThis will DELETE:\n✗ ALL assets from AWS S3\n✗ ALL database records\n✗ ALL settings and configurations\n✗ ALL analytics and logs\n\n⚠️ THIS CANNOT BE UNDONE! ⚠️\n\nType the user's name to confirm: "${userName}"`;

    const userInput = prompt(confirmMessage);

    if (userInput !== userName) {
      if (userInput !== null) {
        alert('Client name does not match. Deletion cancelled.');
      }
      return;
    }

    const finalConfirm = window.confirm(`FINAL CONFIRMATION\n\nAre you absolutely sure you want to delete ${userName}?\n\nThis is your last chance to cancel.`);

    if (!finalConfirm) {
      return;
    }

    setIsDeleting(true);
    setBulkDeleteProgress({ status: 'processing', message: 'Deleting client and all assets...' });

    try {
      const response = await axios.delete(`/api/delete-client/${selectedUserId}`, {
        data: { confirmDelete: true }
      });

      console.log('Client deletion results:', response.data);

      setBulkDeleteProgress({
        status: 'completed',
        message: 'Client deleted successfully'
      });

      alert(`Client deleted successfully!\n\nS3 Files Deleted: ${response.data.results.summary.successfulS3Deletions}/${response.data.results.summary.totalS3Files}\nDatabase Records: ${response.data.results.summary.totalDocumentsDeleted} documents from ${response.data.results.summary.totalModelsDeleted} models\n\nCheck console for detailed results.`);

      // Refresh users list
      await fetchUsersSummary();

      // Clear selected user
      setSelectedUserId('');
      setUserReport(null);
      setUserMenuStructure(null);

    } catch (error) {
      console.error('Error deleting client:', error);
      alert(`Error deleting client: ${error.response?.data?.message || error.message}\n\nCheck console for details.`);
      setBulkDeleteProgress({ status: 'error', error: error.message });
    } finally {
      setIsDeleting(false);
      setTimeout(() => setBulkDeleteProgress(null), 5000);
    }
  };

  return (
    <div className="main-container assets-report">
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px">
          <div className="page-header">
            <div className="row">
              <div className="col-md-6 col-sm-12">
                <div className="title">
                  <h4>Complete Assets Usage Report</h4>
                </div>
              </div>
            </div>
          </div>

          <div className="app">
            <div className="container">
              {/* Users Summary Section */}
              <div className="section">
                <div className="section-header">
                  <h2>Users Assets Summary</h2>
                  <div className="sort-controls">
                    <label>Sort by:</label>
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                      <option value="totalAssets">Total Assets</option>
                      <option value="enabledAssets">Enabled Assets</option>
                      <option value="userName">User Name</option>
                    </select>
                  </div>
                </div>

                {loading && <div className="loading">Loading...</div>}

                <SearchableUserSelect
                  getSortedUsers={getSortedUsers}
                  onUserSelect={handleUserSelect}
                  selectedUserId={selectedUserId}
                />

                {/* Delete Client Button */}
                {selectedUserId && userReport && (
                  <div style={{
                    marginTop: '20px',
                    padding: '20px',
                    backgroundColor: '#fff3cd',
                    border: '2px solid #ffc107',
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                      <div>
                        <h3 style={{ margin: '0 0 8px 0', color: '#856404', fontSize: '18px' }}>
                          ⚠️ Danger Zone
                        </h3>
                        <p style={{ margin: 0, color: '#856404', fontSize: '14px' }}>
                          Permanently delete this client and ALL their data (assets, settings, logs)
                        </p>
                      </div>
                      <button
                        onClick={handleDeleteClient}
                        disabled={isDeleting}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: isDeleting ? '#ccc' : '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: isDeleting ? 'not-allowed' : 'pointer',
                          fontSize: '15px',
                          fontWeight: '600',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          if (!isDeleting) {
                            e.target.style.backgroundColor = '#c82333';
                            e.target.style.transform = 'translateY(-1px)';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!isDeleting) {
                            e.target.style.backgroundColor = '#dc3545';
                            e.target.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        {isDeleting ? '🗑️ Deleting Client...' : '🗑️ Delete Client'}
                      </button>
                    </div>
                  </div>
                )}

                {/* <div className="users-grid">
                  {getSortedUsers().map((user) => (
                    <div
                      key={user.userId}
                      className={`user-card ${selectedUserId === user.userId ? "selected" : ""}`}
                      onClick={() => handleUserSelect(user.userId)}
                    >
                      <h3>{user.userName}</h3>
                      <p className="email">{user.userEmail}</p>
                      <div className="stats">
                        <div className="stat">
                          <span className="label">Total Assets:</span>
                          <span className="value total">{user.totalAssets}</span>
                        </div>
                        <div className="stat">
                          <span className="label">Enabled Assets:</span>
                          <span className="value enabled">{user.enabledAssets}</span>
                        </div>
                        <div className="stat">
                          <span className="label">Linked Buttons:</span>
                          <span className="value linked">{user.linkedButtons}</span>
                        </div>
                        <div className="asset-breakdown">
                          <div className="breakdown-item">
                            <span className="breakdown-label">Topic:</span>
                            <span className="breakdown-value">{user.topicAssets}</span>
                          </div>
                          <div className="breakdown-item">
                            <span className="breakdown-label">Splash:</span>
                            <span className="breakdown-value">{user.splashAssets}</span>
                          </div>
                          <div className="breakdown-item">
                            <span className="breakdown-label">Menu Page:</span>
                            <span className="breakdown-value">{user.menuPageAssets}</span>
                          </div>
                          <div className="breakdown-item">
                            <span className="breakdown-label">Menu Content:</span>
                            <span className="breakdown-value">{user.menuContentAssets}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div> */}
              </div>

              {/* User Menu Structure Section */}
              {userMenuStructure && (
                <div className="section">
                  <div className="section-header">
                    <h2>User Menu Structure & Button Linking</h2>
                    <button
                      className="toggle-button"
                      onClick={() => setShowMenuStructure(!showMenuStructure)}
                    >
                      {showMenuStructure ? 'Hide' : 'Show'} Menu Structure
                    </button>
                  </div>

                  {showMenuStructure && (
                    <div className="menu-structure">
                      <div className="structure-summary">
                        <h3>Linking Summary</h3>
                        <div className="linking-stats">
                          <div className="linking-stat">
                            <span className="stat-label">Linked Button IDs:</span>
                            <div className="btn-ids">
                              {userMenuStructure.linkedBtnIds.map((btnId, index) => (
                                <span key={index} className="btn-id-badge">
                                  {btnId}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="linking-stat">
                            <span className="stat-label">Menu Content Buttons:</span>
                            <span className="stat-value">
                              {userMenuStructure.menuContent.length}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="menu-details">
                        <div className="menu-section">
                          <h4>Frontend Main Menu</h4>
                          <div className="menu-items">
                            {userMenuStructure.frontendMainMenu.map((menu, index) => (
                              <div key={index} className="menu-item">
                                <span className="menu-name">{menu.name}</span>
                                <span
                                  className={`menu-status ${menu.isVisible ? 'visible' : 'hidden'}`}
                                >
                                  {menu.isVisible ? 'Visible' : 'Hidden'}
                                </span>
                                {menu.linkbtnId && (
                                  <span className="linked-btn">→ {menu.linkbtnId}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="menu-section">
                          <h4>Primary Menus & Submenus</h4>
                          <div className="primary-menus">
                            {userMenuStructure.primaryMenus.map((menu, index) => (
                              <div key={index} className="primary-menu">
                                <div className="menu-item primary">
                                  <span className="menu-name">{menu.name}</span>
                                  <span
                                    className={`menu-status ${menu.isVisible ? 'visible' : 'hidden'}`}
                                  >
                                    {menu.isVisible ? 'Visible' : 'Hidden'}
                                  </span>
                                </div>
                                {menu.submenus.length > 0 && (
                                  <div className="submenus">
                                    {menu.submenus.map((submenu, subIndex) => (
                                      <div key={subIndex} className="menu-item submenu">
                                        <span className="menu-name">└ {submenu.name}</span>
                                        <span
                                          className={`menu-status ${submenu.isVisible ? 'visible' : 'hidden'}`}
                                        >
                                          {submenu.isVisible ? 'Visible' : 'Hidden'}
                                        </span>
                                        {submenu.linkbtnId && (
                                          <span className="linked-btn">→ {submenu.linkbtnId}</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Detailed Report Section */}
              {userReport && (
                <div className="section">
                  <h2>Detailed Assets Report</h2>

                  <div className="report-summary">
                    <div className="summary-card">
                      <h3>Overview</h3>
                      <div className="summary-stats">
                        <div className="summary-stat">
                          <span className="number">{userReport.totalAssets}</span>
                          <span className="label">Total Assets</span>
                        </div>
                        <div className="summary-stat">
                          <span className="number">{userReport.enabledAssets}</span>
                          <span className="label">Enabled Assets</span>
                        </div>
                        <div className="summary-stat">
                          <span className="number">{userReport.disabledAssets}</span>
                          <span className="label">Disabled Assets</span>
                        </div>
                        <div className="summary-stat">
                          <span className="number">{userReport.utilizationRate}%</span>
                          <span className="label">Utilization Rate</span>
                        </div>
                      </div>
                    </div>

                    <div className="summary-card">
                      <h3>Button Linking Info</h3>
                      <div className="linking-info">
                        <div className="info-item">
                          <span className="info-label">Linked Buttons:</span>
                          <span className="info-value">{userReport.linkedBtnIds.length}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Content Buttons:</span>
                          <span className="info-value">{userReport.menuContentButtons}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Menu Content Assets:</span>
                          <span className="info-value">
                            {userReport.summary.menuPageContentAssets}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Filter Controls */}
                  <div className="filter-controls">
                    <h3>Filter Assets</h3>
                    <div className="filter-buttons">
                      <button
                        className={filterCategory === 'all' ? 'active' : ''}
                        onClick={() => {
                          setFilterCategory('all');
                          setCurrentPage(1);
                        }}
                      >
                        All Assets ({userReport.assetDetails.length})
                      </button>
                      <button
                        className={filterCategory === 'topic' ? 'active' : ''}
                        onClick={() => {
                          setFilterCategory('topic');
                          setCurrentPage(1);
                        }}
                      >
                        Topic Settings ({userReport.summary.topicSettingsAssets})
                      </button>
                      <button
                        className={filterCategory === 'splash' ? 'active' : ''}
                        onClick={() => {
                          setFilterCategory('splash');
                          setCurrentPage(1);
                        }}
                      >
                        Splash Settings ({userReport.summary.splashSettingsAssets})
                      </button>
                      <button
                        className={filterCategory === 'menu page settings' ? 'active' : ''}
                        onClick={() => {
                          setFilterCategory('menu page settings');
                          setCurrentPage(1);
                        }}
                      >
                        Menu Page ({userReport.summary.menuPageSettingsAssets})
                      </button>
                      <button
                        className={filterCategory === 'menu page content' ? 'active' : ''}
                        onClick={() => {
                          setFilterCategory('menu page content');
                          setCurrentPage(1);
                        }}
                      >
                        Menu Content ({userReport.summary.menuPageContentAssets})
                      </button>
                      <button
                        className={filterCategory === 'wayfinder' ? 'active' : ''}
                        onClick={() => {
                          setFilterCategory('wayfinder');
                          setCurrentPage(1);
                        }}
                      >
                        Wayfinder ({(userReport.summary.wayfinderCategoryAssets || 0) + 
                                    (userReport.summary.wayfinderSubCategoryAssets || 0) + 
                                    (userReport.summary.wayfinderLocationAssets || 0) + 
                                    (userReport.summary.wayfinderBrandingAssets || 0) + 
                                    (userReport.summary.wayfinderMainCategoryAssets || 0) + 
                                    (userReport.summary.wayfinderMainLocationAssets || 0) + 
                                    (userReport.summary.wayfinderMainSubCategoryAssets || 0) + 
                                    (userReport.summary.wayfinderMobileScreenAssets || 0)})
                      </button>
                      <button
                        className={filterCategory === 'global' ? 'active' : ''}
                        onClick={() => {
                          setFilterCategory('global');
                          setCurrentPage(1);
                        }}
                      >
                        Global & Login ({(userReport.summary.globalSettingsAssets || 0) + 
                                        (userReport.summary.loginCustomizationAssets || 0)})
                      </button>
                    </div>

                    {/* Language Filter */}
                    <div className="language-filter" style={{ marginTop: '15px' }}>
                      <h3>Filter by Language</h3>
                      <div className="filter-buttons">
                        <button
                          className={filterLanguage === 'all' ? 'active' : ''}
                          onClick={() => {
                            setFilterLanguage('all');
                            setCurrentPage(1);
                          }}
                        >
                          All Languages
                        </button>
                        {getAvailableLanguages().map(lang => (
                          <button
                            key={lang}
                            className={filterLanguage === lang ? 'active' : ''}
                            onClick={() => {
                              setFilterLanguage(lang);
                              setCurrentPage(1);
                            }}
                          >
                            {lang === 'legacy' ? 'Legacy (No Language)' : lang === 'N/A' ? 'N/A' : lang.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* File Type Filter */}
                  {userReport && (
                    <div className="file-type-filter" style={{
                      padding: '15px',
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      marginBottom: '15px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: '600' }}>
                        Filter by File Type
                      </h4>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button
                          className={fileTypeFilter === 'all' ? 'active' : ''}
                          onClick={() => {
                            setFileTypeFilter('all');
                            setCurrentPage(1);
                            setSelectedAssets([]);
                            setSelectAll(false);
                          }}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: fileTypeFilter === 'all' ? '#007bff' : '#f8f9fa',
                            color: fileTypeFilter === 'all' ? 'white' : '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          All Files ({getFileTypeCounts().total})
                        </button>
                        <button
                          className={fileTypeFilter === 'images' ? 'active' : ''}
                          onClick={() => {
                            setFileTypeFilter('images');
                            setCurrentPage(1);
                            setSelectedAssets([]);
                            setSelectAll(false);
                          }}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: fileTypeFilter === 'images' ? '#28a745' : '#f8f9fa',
                            color: fileTypeFilter === 'images' ? 'white' : '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          🖼️ Images Only ({getFileTypeCounts().images})
                        </button>
                        <button
                          className={fileTypeFilter === 'videos' ? 'active' : ''}
                          onClick={() => {
                            setFileTypeFilter('videos');
                            setCurrentPage(1);
                            setSelectedAssets([]);
                            setSelectAll(false);
                          }}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: fileTypeFilter === 'videos' ? '#dc3545' : '#f8f9fa',
                            color: fileTypeFilter === 'videos' ? 'white' : '#333',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          🎥 Videos Only ({getFileTypeCounts().videos})
                        </button>
                      </div>
                      {fileTypeFilter !== 'all' && (
                        <p style={{ 
                          margin: '10px 0 0 0', 
                          fontSize: '13px', 
                          color: '#666',
                          fontStyle: 'italic'
                        }}>
                          Showing only {fileTypeFilter} • {getFilteredAssets().filter(a => a.hasFile).length} assets match your filters
                        </p>
                      )}
                    </div>
                  )}

                  {/* Bulk Delete Controls */}
                  {getFilteredAssets().filter(a => a.hasFile).length > 0 && (
                    <div className="bulk-delete-controls" style={{
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '10px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span style={{ fontWeight: '500' }}>Select All ({getFilteredAssets().filter(a => a.hasFile).length} assets)</span>
                        </label>
                        {selectedAssets.length > 0 && (
                          <span style={{ 
                            padding: '4px 12px', 
                            backgroundColor: '#007bff', 
                            color: 'white', 
                            borderRadius: '12px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            {selectedAssets.length} selected
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        {selectedAssets.length > 0 && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedAssets([]);
                                setSelectAll(false);
                              }}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              Clear Selection
                            </button>
                            <button
                              onClick={handleBulkDelete}
                              disabled={isDeleting}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: isDeleting ? '#ccc' : '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isDeleting ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                              }}
                            >
                              {isDeleting ? 'Deleting...' : `Delete ${selectedAssets.length} Asset(s)`}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Bulk Delete Progress */}
                  {bulkDeleteProgress && (
                    <div style={{
                      padding: '15px',
                      backgroundColor: bulkDeleteProgress.status === 'error' ? '#f8d7da' : bulkDeleteProgress.status === 'completed' ? '#d4edda' : '#fff3cd',
                      border: `1px solid ${bulkDeleteProgress.status === 'error' ? '#f5c6cb' : bulkDeleteProgress.status === 'completed' ? '#c3e6cb' : '#ffeaa7'}`,
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}>
                      <h4 style={{ margin: '0 0 10px 0' }}>
                        {bulkDeleteProgress.status === 'processing' && '⏳ Deleting assets...'}
                        {bulkDeleteProgress.status === 'completed' && '✅ Bulk delete completed'}
                        {bulkDeleteProgress.status === 'error' && '❌ Error during bulk delete'}
                      </h4>
                      {bulkDeleteProgress.status === 'processing' && (
                        <p>Progress: {bulkDeleteProgress.current} / {bulkDeleteProgress.total}</p>
                      )}
                      {bulkDeleteProgress.status === 'completed' && (
                        <div>
                          <p>Successful: {bulkDeleteProgress.successful} | Failed: {bulkDeleteProgress.failed}</p>
                        </div>
                      )}
                      {bulkDeleteProgress.status === 'error' && (
                        <p>Error: {bulkDeleteProgress.error}</p>
                      )}
                    </div>
                  )}

                  <div className="assets-table">
                    <h3>Assets Details</h3>
                    <div className="table-container">
                      <table>
                        <thead>
                          <tr>
                            <th style={{ width: '50px' }}>
                              <input
                                type="checkbox"
                                checked={selectAll}
                                onChange={handleSelectAll}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                title="Select all assets with files"
                              />
                            </th>
                            <th>Category</th>
                            <th>Language</th>
                            <th>Field</th>
                            <th>File Name</th>
                            <th>File URL</th>
                            <th>Status</th>
                            <th>Has File</th>
                            <th>Button ID</th>
                            <th>Location ID</th>
                            <th>Content Type</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getPaginatedAssets().map((asset, index) => (
                            <tr key={index} className={asset.enabled ? 'enabled' : 'disabled'}>
                              <td style={{ textAlign: 'center' }}>
                                {asset.hasFile ? (
                                  <input
                                    type="checkbox"
                                    checked={isAssetSelected(asset)}
                                    onChange={() => handleSelectAsset(asset)}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                  />
                                ) : (
                                  <span style={{ color: '#ccc' }}>-</span>
                                )}
                              </td>
                              <td>
                                <span
                                  className={`category-badge ${asset.category.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  {asset.category}
                                </span>
                              </td>
                              <td>
                                <span className={`language-badge ${asset.language === 'legacy' ? 'legacy' : ''}`}>
                                  {asset.language === 'legacy' ? 'Legacy' : asset.language === 'N/A' ? 'N/A' : asset.language.toUpperCase()}
                                </span>
                              </td>
                              <td>{asset.field}</td>
                              <td className="filename">{asset.fileName}</td>
                              <td className="file-url">
                                {asset.fileUrl !== 'N/A' ? (
                                  <div className="url-container">
                                    <a
                                      href={asset.fileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="url-link"
                                      title={asset.fileUrl}
                                    >
                                      {asset.fileUrl.length > 30
                                        ? `${asset.fileUrl.substring(0, 30)}...`
                                        : asset.fileUrl}
                                    </a>
                                    <button
                                      className="copy-btn"
                                      onClick={() => copyToClipboard(asset.fileUrl)}
                                      title="Copy URL"
                                    >
                                      📋
                                    </button>
                                  </div>
                                ) : (
                                  'N/A'
                                )}
                              </td>
                              <td>
                                <span
                                  className={`status ${asset.enabled ? 'enabled' : 'disabled'}`}
                                >
                                  {asset.enabled ? 'Enabled' : 'Disabled'}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`file-status ${asset.hasFile ? 'has-file' : 'no-file'}`}
                                >
                                  {asset.hasFile ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td>{asset.btnId || 'N/A'}</td>
                              <td>{asset.locationId || 'N/A'}</td>
                              <td>{asset.contentType || 'N/A'}</td>

                              <td className="dimensions">
                                {asset.hasFile ? (
                                  <i
                                    className="dw dw-delete-3"
                                    style={{ fontSize: '18px', color: '#000', cursor: 'pointer' }}
                                    onClick={() =>
                                      confirmDelete(
                                        asset.rowid,
                                        asset.fileUrl,
                                        asset.field,
                                        asset.category,
                                        asset.language
                                      )
                                    }
                                  ></i>
                                ) : (
                                  ''
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="pagination-container">
                      <div className="pagination-info">
                        <span>
                          Showing{' '}
                          {Math.min(
                            (currentPage - 1) * itemsPerPage + 1,
                            getFilteredAssets().length
                          )}{' '}
                          to {Math.min(currentPage * itemsPerPage, getFilteredAssets().length)} of{' '}
                          {getFilteredAssets().length} assets
                        </span>
                        <div className="items-per-page">
                          <label>Items per page:</label>
                          <select
                            value={itemsPerPage}
                            onChange={e => handleItemsPerPageChange(Number(e.target.value))}
                          >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>

                      <div className="pagination-controls">
                        <button
                          onClick={() => handlePageChange(1)}
                          disabled={currentPage === 1}
                          className="pagination-btn"
                        >
                          First
                        </button>
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="pagination-btn"
                        >
                          Previous
                        </button>

                        <div className="page-numbers">
                          {Array.from({ length: Math.min(5, getTotalPages()) }, (_, i) => {
                            let pageNum;
                            if (getTotalPages() <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= getTotalPages() - 2) {
                              pageNum = getTotalPages() - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }

                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === getTotalPages()}
                          className="pagination-btn"
                        >
                          Next
                        </button>
                        <button
                          onClick={() => handlePageChange(getTotalPages())}
                          disabled={currentPage === getTotalPages()}
                          className="pagination-btn"
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetsReport;
